import { Box, Button, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import { decode } from 'html-entities'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useGetSharedFileUrl } from 'src/api/file'
import { RichTextViewer } from 'src/components/common/RichTextViewer'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor, isFileImage } from 'src/utils'
import { FormFieldType } from '../../enums'
import { FormField, FormRow, getFieldKey } from '../../generated-types/form-field'
import { getFormattedDate } from '../../utils'
import { UserDetail, getUserDetail } from '../../utils/getUserDetails'
import { exportSchemaReportPDF } from './exportSchemaReportPDF'

const capitalizeFirst = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '')

const getFileTypeFromUrl = (url: string): string => {
  if (!url) return ''
  const urlPart = url.split('?')[0]
  const extension = urlPart.substring(urlPart.lastIndexOf('.') + 1).toLowerCase()
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg']
  if (imageExtensions.includes(extension)) {
    return `image/${extension === 'jpg' ? 'jpeg' : extension}`
  }
  return ''
}

const formatFieldValue = (
  field: FormField,
  value: unknown,
  t: (k: string, o?: object) => string,
): string | React.ReactNode => {
  if (value == null || value === '') return '—'
  switch (field.type) {
    case FormFieldType.datePicker:
      return getFormattedDate(new Date(value as string))
    case FormFieldType.dateTime:
      return getFormattedDate(new Date(value as string), 'YYYY-MM-DD HH:mm A')
    case FormFieldType.checkBox:
      return value ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' })
    case FormFieldType.checkBoxList: {
      try {
        const list = typeof value === 'string' ? JSON.parse(value || '[]') : value
        return Array.isArray(list) ? list.join(', ') : String(value)
      } catch {
        return String(value)
      }
    }
    default:
      return String(value)
  }
}

const FormFieldRow = ({ field, value }: { field: FormField; value: unknown }) => {
  const { t } = useTranslation(['common', 'overall-reports'])
  const { openFilePreviewDialog, openImagePreviewDialog } = useDialogCtx()
  const { data: sharedUrl } = useGetSharedFileUrl(value as string)

  const handleFilePreview = (e: React.MouseEvent) => {
    if (!sharedUrl) return
    e.preventDefault()
    e.stopPropagation()

    if (isFileImage({ contentType: getFileTypeFromUrl(value as string) } as any)) {
      openImagePreviewDialog(sharedUrl, field.name, field.name)
    } else {
      openFilePreviewDialog(sharedUrl.split('?')[0], field.name)
    }
  }

  if (field.type === FormFieldType.title) {
    return (
      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
        {field.name}
      </Typography>
    )
  }
  if (
    field.type === FormFieldType.richText ||
    field.type === FormFieldType.instructionImage ||
    field.type === FormFieldType.instructionFile
  ) {
    if (!field.showInReport) return null
    if (field.type === FormFieldType.richText) {
      return <RichTextViewer html={decode(field.content ?? '')} maxHeight={300} />
    }
    if (field.type === FormFieldType.instructionImage) {
      return field.content ? (
        <img
          src={field.content}
          alt="instruction"
          style={{ maxWidth: `${field.imageWidth ?? 100}%`, borderRadius: 4 }}
        />
      ) : null
    }
    // instructionFile
    return field.content ? (
      <a
        href={field.content}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {field.name}
      </a>
    ) : null
  }
  // Handle file upload field
  if (field.type === FormFieldType.fileUpload) {
    if (!value)
      return (
        <>
          <Typography variant="body2" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
            {field.name}
          </Typography>
          <Typography variant="body2" display="block" sx={{ wordBreak: 'break-word' }}>
            —
          </Typography>
        </>
      )
    return (
      <>
        <Typography variant="body2" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
          {field.name}
        </Typography>
        <Typography
          variant="body2"
          display="block"
          sx={{ wordBreak: 'break-word', cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
          onClick={handleFilePreview}
        >
          {t('uploaded-file-link', { ns: 'overall-reports' })}
        </Typography>
      </>
    )
  }

  const displayValue = formatFieldValue(field, value, t)
  return (
    <>
      <Typography variant="body2" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
        {field.name}
      </Typography>
      <Typography variant="body2" display="block" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
        {displayValue}
      </Typography>
    </>
  )
}

interface ReportViewerProps {
  layouts: FormRow[]
  schema: { name: string; description?: string }
  report: { values: string }
  pop?: () => void
  disabled?: boolean
  createdAt?: string
  createdByUser?: { fullName?: string; firstName?: string; lastName?: string }
  linkedEntity?: string
  locality?: string
  embedded?: boolean
}

const ReportViewer = ({
  layouts,
  schema,
  report,
  pop,
  disabled = false,
  createdAt,
  createdByUser,
  linkedEntity,
  locality,
  embedded = false,
}: ReportViewerProps) => {
  const { t } = useTranslation(['common', 'overall-reports'])

  const reportData = JSON.parse(report.values || '{}')

  const content = (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1.5}>
        <Button
          variant="contained"
          onClick={() =>
            exportSchemaReportPDF(report, layouts, t, { schema, createdAt, createdByUser, linkedEntity, locality })
          }
          disabled={disabled}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {t('export')}
        </Button>
      </Box>
      <Box id="pdf-viewer" sx={{ p: 1.5 }}>
        {!embedded && (
          <>
            <Typography variant="h6" gutterBottom>
              {capitalizeFirst(schema.name)}
            </Typography>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, mt: 2 }}>
              {t('details', { ns: 'common' })}
            </Typography>
            <Box
              sx={th => ({
                p: 2,
                mb: 2,
                borderRadius: 1.5,
                bgcolor: `${getThemeColor(th, MUIThemeColors.primaryMain)}0D`,
                border: `1px solid ${getThemeColor(th, MUIThemeColors.secondaryLight)}`,
              })}
            >
              <Grid container spacing={2} sx={{ fontSize: '0.875rem' }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {schema.description && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" display="block">
                          {t('description', { ns: 'common' })}
                        </Typography>
                        <Typography variant="body2" display="block">
                          {schema.description}
                        </Typography>
                      </Box>
                    )}
                    {linkedEntity && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" display="block">
                          {t('linked-entity', { ns: 'common' })}
                        </Typography>
                        <Typography variant="body2" display="block">
                          {linkedEntity}
                        </Typography>
                      </Box>
                    )}
                    {locality && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" display="block">
                          {t('locality', { ns: 'common' })}
                        </Typography>
                        <Typography variant="body2" display="block">
                          {locality}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" display="block">
                        {t('filled-date', { ns: 'common' })}
                      </Typography>
                      <Typography variant="body2" display="block">
                        {createdAt ? getFormattedDate(new Date(createdAt), 'YYYY-MM-DD HH:mm:ss') : '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" display="block">
                        {t('createdBy', { ns: 'common' })}
                      </Typography>
                      <Typography variant="body2" display="block">
                        {getUserDetail(createdByUser, UserDetail.FullName)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t('form-data', { ns: 'common' })}
            </Typography>
          </>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {layouts.map((row, rowIdx) => (
            <Grid key={rowIdx} container spacing={2}>
              {(row.columns || []).map((col, colIdx) => (
                <Grid item xs={12} md={12 / (row.columns?.length || 1)} key={colIdx}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(col.fields || []).map((field, fieldIdx) => (
                      <Box key={fieldIdx} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormFieldRow field={field} value={reportData[getFieldKey(field)] ?? reportData[field.name]} />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      </Box>
    </>
  )
  if (embedded) return content
  return (
    <>
      <DialogContent className="scrollbar">{content}</DialogContent>
      <DialogActions>
        <Button onClick={pop}>{t('cancel')}</Button>
      </DialogActions>
    </>
  )
}

export default ReportViewer
